import { createTheme } from '@mui/material/styles';
import { BRAND, RADIUS, SHADOW } from './fitisticTokens';

/**
 * Builds the MUI theme for the admin portal using the Fitistic app's
 * design language (electric royal blue + midnight navy + slate neutrals).
 */
export default function createFitisticTheme(mode = 'light') {
  const light = mode === 'light';

  const c = {
    primary: light ? BRAND.primary : '#8B5CF6',
    primaryDark: light ? BRAND.primaryDark : BRAND.primary,
    primaryLight: light ? BRAND.primaryLight : '#A78BFA',
    primarySoft: light ? BRAND.primarySoft : 'rgba(124,58,237,0.16)',
    primarySoftHover: light ? '#EDE9FE' : 'rgba(124,58,237,0.24)',
    bg: light ? BRAND.bg : '#0B1120',
    surface: light ? BRAND.surface : BRAND.cardDark,
    surfaceSoft: light ? BRAND.surfaceSoft : BRAND.cardDarkSurface,
    textDark: light ? BRAND.textDark : '#F1F5F9',
    textMid: light ? BRAND.textMid : '#CBD5E1',
    textLight: light ? BRAND.textLight : '#64748B',
    divider: light ? BRAND.divider : BRAND.cardDarkBorder,
    hover: light ? '#F1F5F9' : '#1E293B',
  };

  const focusRing = `0 0 0 3px ${light ? 'rgba(124,58,237,0.18)' : 'rgba(139,92,246,0.30)'}`;

  return createTheme({
    palette: {
      mode,
      primary: { main: c.primary, light: c.primaryLight, dark: c.primaryDark, contrastText: '#FFFFFF' },
      secondary: {
        main: light ? BRAND.textDark : '#E2E8F0',
        light: light ? BRAND.textMid : '#F8FAFC',
        dark: light ? '#020617' : '#CBD5E1',
        contrastText: light ? '#FFFFFF' : BRAND.textDark,
      },
      background: { default: c.bg, paper: c.surface },
      text: { primary: c.textDark, secondary: c.textMid, disabled: c.textLight },
      divider: c.divider,
      action: { hover: c.hover, selected: c.primarySoft },
      error:   { main: BRAND.red,   light: light ? BRAND.redLight : '#450A0A', dark: '#B91C1C' },
      success: { main: BRAND.green, light: light ? BRAND.greenLight : '#022C22', dark: '#047857' },
      warning: { main: BRAND.gold,  light: light ? BRAND.goldLight : '#451A03', dark: '#B45309' },
      info:    { main: c.primary,   light: c.primarySoft, dark: c.primaryDark },
    },
    typography: {
      fontFamily: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
      fontSize: 14,
      h1: { fontWeight: 800, fontSize: '2.25rem', letterSpacing: '-0.02em', lineHeight: 1.2 },
      h2: { fontWeight: 800, fontSize: '1.875rem', letterSpacing: '-0.02em', lineHeight: 1.2 },
      h3: { fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.01em', lineHeight: 1.25 },
      h4: { fontWeight: 800, fontSize: '1.375rem', lineHeight: 1.25 },
      h5: { fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.3 },
      h6: { fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      body1: { fontSize: '0.875rem', lineHeight: 1.55, color: c.textDark },
      body2: { fontSize: '0.8125rem', lineHeight: 1.55, color: c.textMid },
      caption: { fontSize: '0.75rem', lineHeight: 1.5, color: c.textMid },
      button: { textTransform: 'none', fontWeight: 600, fontSize: '0.875rem' },
    },
    shape: { borderRadius: RADIUS.md },
    shadows: [
      'none',
      SHADOW.subtle,
      SHADOW.card,
      '0 4px 12px rgba(15,23,42,0.08)',
      '0 8px 20px rgba(15,23,42,0.10)',
      '0 12px 28px rgba(15,23,42,0.12)',
      '0 16px 40px rgba(15,23,42,0.14)',
      ...Array(18).fill('0 16px 40px rgba(15,23,42,0.14)'),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: (t) => ({
          html: { scrollBehavior: 'smooth', colorScheme: mode },
          body: {
            backgroundColor: t.palette.background.default,
            color: t.palette.text.primary,
            fontFamily: t.typography.fontFamily,
            WebkitFontSmoothing: 'antialiased',
          },
          '*': { boxSizing: 'border-box' },
          '::selection': { backgroundColor: 'rgba(124,58,237,0.18)' },
          '::-webkit-scrollbar': { width: 10, height: 10 },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: light ? '#CBD5E1' : '#334155',
            borderRadius: 8,
            border: `3px solid ${t.palette.background.default}`,
            '&:hover': { backgroundColor: light ? '#94A3B8' : '#475569' },
          },
          '::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          '@media (prefers-reduced-motion: reduce)': {
            '*': { animationDuration: '0.01ms !important', transitionDuration: '0.01ms !important' },
          },
        }),
      },

      // ── Buttons ────────────────────────────────────────────────────────────
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md,
            padding: '7px 16px',
            fontWeight: 600,
            minHeight: 36,
            transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:focus-visible': { boxShadow: focusRing },
          },
          containedPrimary: {
            background: c.primary,
            color: '#FFFFFF',
            boxShadow: '0 2px 10px rgba(124,58,237,0.25)',
            '&:hover': {
              background: c.primaryDark,
              boxShadow: '0 4px 16px rgba(124,58,237,0.35)',
              transform: 'translateY(-1px)',
            },
            '&:active': { transform: 'translateY(0)' },
            '&.Mui-disabled': { background: c.divider, color: c.textLight, boxShadow: 'none' },
          },
          outlined: {
            borderColor: c.divider,
            backgroundColor: c.surface,
            color: c.textDark,
            '&:hover': {
              backgroundColor: c.surfaceSoft,
              borderColor: light ? '#CBD5E1' : '#334155',
            },
          },
          outlinedPrimary: {
            color: c.primary,
            borderColor: light ? BRAND.primarySoftBorder : 'rgba(139,92,246,0.4)',
            '&:hover': { backgroundColor: c.primarySoft, borderColor: c.primary },
          },
          text: { '&:hover': { backgroundColor: c.primarySoft } },
          sizeSmall: { padding: '4px 12px', fontSize: '0.8125rem', minHeight: 30, borderRadius: RADIUS.sm },
          sizeLarge: { padding: '10px 22px', fontSize: '0.9375rem', minHeight: 44, borderRadius: RADIUS.lg },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md,
            color: c.textMid,
            transition: 'all 0.15s',
            '&:hover': { backgroundColor: c.hover, color: c.textDark },
            '&:focus-visible': { boxShadow: focusRing },
          },
        },
      },
      MuiFab: {
        styleOverrides: { primary: { background: c.primary, boxShadow: '0 4px 16px rgba(124,58,237,0.35)' } },
      },

      // ── Surfaces ───────────────────────────────────────────────────────────
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: 'none',
            border: `1px solid ${theme.palette.divider}`,
          }),
          rounded: { borderRadius: RADIUS.lg },
          elevation1: { boxShadow: SHADOW.card },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: RADIUS.lg,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: SHADOW.subtle,
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
          }),
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: 20, '&:last-child': { paddingBottom: 20 } } },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.divider,
          }),
        },
      },

      // ── Tables ─────────────────────────────────────────────────────────────
      MuiTableContainer: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: RADIUS.lg,
            border: `1px solid ${theme.palette.divider}`,
            overflowX: 'auto',
            backgroundColor: theme.palette.background.paper,
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
            padding: '12px 16px',
            fontSize: '0.875rem',
            color: theme.palette.text.primary,
          }),
          head: ({ theme }) => ({
            fontWeight: 700,
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            backgroundColor: c.surfaceSoft,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            whiteSpace: 'nowrap',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiTableRow: {
        defaultProps: { hover: true },
        styleOverrides: {
          root: {
            '&:last-child td': { borderBottom: 0 },
            transition: 'background-color 0.15s ease',
            '&.MuiTableRow-hover:hover': { backgroundColor: light ? '#F8FAFF' : '#162036' },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: { root: { borderTop: 'none' }, toolbar: { minHeight: 52 } },
      },

      // ── Chips ──────────────────────────────────────────────────────────────
      MuiChip: {
        styleOverrides: {
          root: ({ theme, ownerState }) => {
            const tones = {
              success: [light ? BRAND.greenLight : '#022C22', light ? '#047857' : '#6EE7B7', light ? '#A7F3D0' : '#065F46'],
              warning: [light ? BRAND.goldLight : '#451A03', light ? '#B45309' : '#FCD34D', light ? '#FDE68A' : '#78350F'],
              error:   [light ? BRAND.redLight : '#450A0A', light ? '#B91C1C' : '#FCA5A5', light ? '#FECACA' : '#7F1D1D'],
              primary: [c.primarySoft, light ? BRAND.primaryDark : '#93C5FD', light ? BRAND.primarySoftBorder : '#1E3A8A'],
              info:    [c.primarySoft, light ? BRAND.primaryDark : '#93C5FD', light ? BRAND.primarySoftBorder : '#1E3A8A'],
              secondary: [c.surfaceSoft, c.textDark, c.divider],
            };
            const tone = tones[ownerState.color];
            const base = {
              fontWeight: 600,
              fontSize: '0.75rem',
              borderRadius: RADIUS.sm,
              height: 26,
              '& .MuiChip-label': { paddingLeft: 10, paddingRight: 10, whiteSpace: 'nowrap' },
            };
            if (ownerState.variant === 'outlined') {
              return {
                ...base,
                backgroundColor: 'transparent',
                ...(tone ? { color: tone[1], borderColor: tone[2] } : { color: theme.palette.text.primary, borderColor: theme.palette.divider }),
              };
            }
            if (tone) return { ...base, backgroundColor: tone[0], color: tone[1], border: `1px solid ${tone[2]}` };
            return { ...base, backgroundColor: c.surfaceSoft, color: c.textMid, border: `1px solid ${c.divider}` };
          },
          sizeSmall: { height: 22, fontSize: '0.6875rem', '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 } },
        },
      },

      // ── Inputs ─────────────────────────────────────────────────────────────
      MuiTextField: {
        defaultProps: { size: 'small', InputLabelProps: { shrink: true }, autoComplete: 'off' },
        styleOverrides: {
          root: {
            '& .MuiFormHelperText-root': { fontSize: '0.75rem', marginTop: 4, marginLeft: 2 },
          },
        },
      },
      MuiSelect: {
        defaultProps: { size: 'small' },
        styleOverrides: { root: { borderRadius: RADIUS.md, fontSize: '0.875rem' } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: RADIUS.md,
            backgroundColor: theme.palette.background.paper,
            fontSize: '0.875rem',
            transition: 'box-shadow 0.15s ease',
            marginTop: 0,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider, top: 0 },
            '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: light ? '#CBD5E1' : '#334155' },
            '&.Mui-focused': { boxShadow: focusRing },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main, borderWidth: 1 },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.error.main },
          }),
        },
      },
      MuiInputLabel: {
        defaultProps: { shrink: true },
        styleOverrides: {
          root: ({ theme }) => ({
            position: 'relative',
            transform: 'none',
            fontSize: '0.8125rem',
            fontWeight: 600,
            color: theme.palette.text.primary,
            marginBottom: 6,
            '&.Mui-focused': { color: theme.palette.primary.main },
          }),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: { fontSize: '0.875rem' },
          input: ({ theme }) => ({
            '&::placeholder': { color: c.textLight, opacity: 1 },
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
              WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              caretColor: theme.palette.text.primary,
              borderRadius: 'inherit',
            },
            colorScheme: mode,
          }),
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            fontWeight: 600,
            '&.Mui-focused': { color: theme.palette.primary.main },
          }),
        },
      },
      MuiInputAdornment: {
        styleOverrides: { root: { color: c.textLight, '& .MuiTypography-root': { color: c.textMid, fontWeight: 600 } } },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { '&.Mui-checked + .MuiSwitch-track': { backgroundColor: c.primary, opacity: 1 } },
          track: { backgroundColor: light ? '#CBD5E1' : '#334155', opacity: 1 },
        },
      },

      // ── Menus & overlays ───────────────────────────────────────────────────
      MuiMenu: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          paper: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: RADIUS.lg,
            boxShadow: '0 16px 40px rgba(15,23,42,0.14)',
            padding: 4,
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: RADIUS.sm,
            margin: '2px 4px',
            minHeight: 36,
            fontSize: '0.875rem',
            color: theme.palette.text.primary,
            '&:hover': { backgroundColor: c.hover },
            '&.Mui-selected': {
              backgroundColor: c.primarySoft,
              color: light ? BRAND.primaryDark : '#93C5FD',
              fontWeight: 600,
              '&:hover': { backgroundColor: c.primarySoftHover },
            },
          }),
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: RADIUS.xl, border: 'none', boxShadow: '0 24px 64px rgba(15,23,42,0.24)' },
        },
      },
      MuiBackdrop: {
        styleOverrides: {
          root: { '&:not(.MuiBackdrop-invisible)': { backgroundColor: 'rgba(9,13,22,0.55)', backdropFilter: 'blur(3px)' } },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: ({ theme }) => ({
            fontSize: '1.0625rem',
            fontWeight: 700,
            padding: '18px 24px',
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: { padding: '20px 24px' },
          dividers: ({ theme }) => ({ borderColor: theme.palette.divider, padding: 24 }),
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({ theme }) => ({
            padding: '14px 24px',
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: c.surfaceSoft,
            gap: 8,
          }),
        },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: {
            fontSize: '0.75rem',
            borderRadius: RADIUS.sm,
            backgroundColor: BRAND.cardDark,
            color: '#FFFFFF',
            padding: '6px 10px',
            fontWeight: 500,
          },
          arrow: { color: BRAND.cardDark },
        },
      },
      MuiSnackbarContent: {
        styleOverrides: { root: { borderRadius: 12, backgroundColor: BRAND.textDark } },
      },

      // ── Feedback ───────────────────────────────────────────────────────────
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: RADIUS.md, fontSize: '0.875rem', alignItems: 'center' },
          standardSuccess: { backgroundColor: light ? '#ECFDF5' : '#022C22', color: light ? '#047857' : '#6EE7B7', border: `1px solid ${light ? '#A7F3D0' : '#065F46'}` },
          standardError:   { backgroundColor: light ? '#FEF2F2' : '#450A0A', color: light ? '#B91C1C' : '#FCA5A5', border: `1px solid ${light ? '#FECACA' : '#7F1D1D'}` },
          standardWarning: { backgroundColor: light ? '#FFFBEB' : '#451A03', color: light ? '#B45309' : '#FCD34D', border: `1px solid ${light ? '#FDE68A' : '#78350F'}` },
          standardInfo:    { backgroundColor: c.primarySoft, color: light ? BRAND.primaryDark : '#93C5FD', border: `1px solid ${light ? BRAND.primarySoftBorder : '#1E3A8A'}` },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 8, borderRadius: 999, backgroundColor: c.surfaceSoft },
          bar: { borderRadius: 999 },
          barColorPrimary: { backgroundColor: c.primary },
        },
      },
      MuiCircularProgress: { defaultProps: { thickness: 4.5 } },
      MuiSkeleton: {
        defaultProps: { animation: 'wave' },
        styleOverrides: { root: { borderRadius: RADIUS.sm, backgroundColor: light ? '#EEF2F7' : '#1E293B' } },
      },

      // ── Navigation ─────────────────────────────────────────────────────────
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 44 },
          indicator: { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: c.primary },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            minHeight: 44,
            color: c.textMid,
            '&:hover': { color: theme.palette.text.primary },
            '&.Mui-selected': { color: theme.palette.primary.main },
          }),
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            borderColor: c.divider,
            '&.Mui-selected': {
              backgroundColor: c.primarySoft,
              color: light ? BRAND.primaryDark : '#93C5FD',
              '&:hover': { backgroundColor: c.primarySoftHover },
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: RADIUS.md,
            '&.Mui-selected': { backgroundColor: c.primarySoft, color: light ? BRAND.primaryDark : '#93C5FD' },
            '&.Mui-selected:hover': { backgroundColor: c.primarySoftHover },
          },
        },
      },
      MuiPagination: {
        styleOverrides: { root: { '& .MuiPaginationItem-root': { borderRadius: RADIUS.sm, fontWeight: 600 } } },
      },
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': { background: c.primary, color: '#FFFFFF', '&:hover': { background: c.primaryDark } },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: { root: { fontWeight: 700, fontSize: '0.875rem' } },
      },
      MuiDivider: {
        styleOverrides: { root: ({ theme }) => ({ borderColor: theme.palette.divider }) },
      },
      MuiAccordion: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: RADIUS.lg,
            border: `1px solid ${theme.palette.divider}`,
            '&:before': { display: 'none' },
          }),
        },
      },
    },
  });
}
